/**
 * Роутинг клиента — ОДИН модуль на все адреса, которые мы строим и разбираем.
 *
 * Почему отдельно и почему hash, а не History API path:
 *  1. Сервер отдаёт SPA одним index.html без rewrite-правил. Path-роутинг
 *     ("/room/!id") требовал бы настройки веб-сервера на любой префикс;
 *     hash-роутинг ("#/room/!id") работает на статической раздаче как есть.
 *  2. Разбор и сборка адреса нужны в трёх местах: App (какую комнату
 *     открыть), Timeline (какую ссылку отдать в «Поделиться»/«Копировать
 *     ссылку») и перехватчик кликов matrix.to (куда вести внутреннюю
 *     навигацию). Три копии encode/decode-логики — это ровно то место, где
 *     однажды в одной забудут decodeURIComponent и алиасы с «#» сломаются.
 *
 * ФОРМАТЫ:
 *  - Наш клиент:  #/room/<roomId>           — открытая комната
 *                 #/room/<roomId>/<eventId> — комната + переход к событию
 *  - matrix.to:   https://matrix.to/#/@user:server          — профиль/DM
 *                 https://matrix.to/#/!roomId:server        — комната
 *                 https://matrix.to/#/!roomId/$eventId      — permalink
 *                 https://matrix.to/#/#alias:server         — комната по алиасу
 *
 * КОДИРОВАНИЕ (гоча из постановки): roomId содержит «!» и «:», eventId —
 * «$», алиасы содержат «#». В fragment всё это легально, НО алиас
 * «#general:server» при разборе split('/') дал бы лишний сегмент. Поэтому
 * при сборке — encodeURIComponent на каждый сегмент, при разборе —
 * decodeURIComponent. matrix.to-ссылки из чужих клиентов могут быть как
 * закодированы, так и нет: разбираем пробуя decodeURIComponent, при ошибке
 * (кривой процент-эскейп) берём сырой сегмент.
 */

/** Цель навигации внутри нашего клиента. */
export interface RouteTarget {
  /**
   * Идентификатор комнаты «!id:server» ИЛИ алиас «#name:server» (ссылка-
   * приглашение, задача Антона 16.10 10:33). Алиас App сначала резолвит
   * через каталог сервера и только потом открывает комнату — в остальной
   * клиент алиас как roomId не уходит.
   */
  roomId: string | null;
  /** Событие, к которому надо проскроллить после открытия комнаты. */
  eventId: string | null;
  /** Серверы-проводники из ?via=… (для join по ссылке). Пусто — нет. */
  via?: string[];
}

const EMPTY_ROUTE: RouteTarget = { roomId: null, eventId: null };

/** Алиас комнаты (#name:server), а не её id. */
export function isRoomAlias(idOrAlias: string): boolean {
  return idOrAlias.startsWith("#");
}

/** Домен из «!id:server» / «#name:server» — сервер-проводник по умолчанию. */
export function serverNameOf(idOrAlias: string): string | null {
  const i = idOrAlias.indexOf(":");
  if (i < 0 || i === idOrAlias.length - 1) return null;
  return idOrAlias.slice(i + 1);
}

/** Безопасный decodeURIComponent: кривой эскейп — не повод падать. */
function safeDecode(seg: string): string {
  try {
    return decodeURIComponent(seg);
  } catch {
    return seg;
  }
}

/** Собрать hash-адрес комнаты (с событием или без). Без ведущего «#». */
export function roomHash(roomId: string, eventId?: string): string {
  const base = `/room/${encodeURIComponent(roomId)}`;
  return eventId ? `${base}/${encodeURIComponent(eventId)}` : base;
}

/** Разобрать наш hash («#/room/…») в цель навигации. */
export function parseLocationHash(hash: string): RouteTarget {
  // hash приходит как "#/room/..." или "" или "#".
  const full = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!full) return EMPTY_ROUTE;
  // Query внутри fragment («#/room/<id>?via=algsuccess.com») — так выглядит
  // ссылка-приглашение. Отрезаем ДО split("/"), иначе «?via=…» прилипал
  // к id комнаты. Сам «?» в закодированном сегменте невозможен
  // (encodeURIComponent кодирует его как %3F), так что первый «?» — граница.
  const q = full.indexOf("?");
  const raw = q >= 0 ? full.slice(0, q) : full;
  const query = q >= 0 ? full.slice(q + 1) : "";
  const segs = raw.split("/").filter((s) => s.length > 0);
  if (segs[0] !== "room") return EMPTY_ROUTE;
  const roomId = segs[1] ? safeDecode(segs[1]) : null;
  const eventId = segs[2] ? safeDecode(segs[2]) : null;
  if (!roomId) return EMPTY_ROUTE;
  // Принимаем только то, что похоже на комнату: «!id» или «#alias».
  // Мусор в адресе не должен уезжать в getRoom/joinRoom.
  if (!roomId.startsWith("!") && !isRoomAlias(roomId)) return EMPTY_ROUTE;
  const via = query
    ? new URLSearchParams(query).getAll("via").map((v) => v.trim()).filter(Boolean)
    : [];
  return via.length > 0 ? { roomId, eventId, via } : { roomId, eventId };
}

/**
 * База ссылок, которые отдаём НАРУЖУ (копировать/поделиться).
 *
 * В браузере и в Mac-оболочке (WKWebView грузит devapp.algsuccess.com) это
 * текущий origin+path. В Android/Capacitor origin — http://localhost: такая
 * ссылка у получателя никуда не ведёт, поэтому там берём публичный адрес
 * приложения (config.appClientUri, по умолчанию devapp.algsuccess.com).
 */
export function publicAppBase(
  loc: { origin: string; pathname: string } = window.location,
  fallback = "https://devapp.algsuccess.com",
): string {
  if (/^https?:\/\/localhost(?::\d+)?$/.test(loc.origin) || !/^https?:/.test(loc.origin)) {
    return `${fallback.replace(/\/+$/, "")}/`;
  }
  return `${loc.origin}${loc.pathname}`;
}

/**
 * ССЫЛКА-ПРИГЛАШЕНИЕ В КОМНАТУ (Антон 16.10 10:33: «у каждой комнаты
 * должна быть ссылка, которой можно поделиться, чтобы пригласить»).
 *
 * Формат — НАШ клиент, а не matrix.to:
 *   с алиасом:  <base>#/room/%23name%3Aalgsuccess.com
 *   без алиаса: <base>#/room/!id%3Aalgsuccess.com?via=algsuccess.com
 *
 * Алиас предпочтительнее: он человекочитаем и переживает апгрейд комнаты.
 * via нужен только для id — по нему сервер получателя найдёт комнату, если
 * сам в ней не состоит (для алиаса сервер берётся из самого алиаса).
 */
export function roomShareLink(
  room: { roomId: string; alias?: string | null },
  base: string = publicAppBase(),
): string {
  if (room.alias && isRoomAlias(room.alias)) {
    return `${base}#${roomHash(room.alias)}`;
  }
  const via = serverNameOf(room.roomId) ?? "algsuccess.com";
  return `${base}#${roomHash(room.roomId)}?via=${encodeURIComponent(via)}`;
}

/**
 * Ссылка на НАШ клиент (любой из известных адресов) → цель навигации.
 * Нужна перехватчику кликов: вставленная в чат ссылка-приглашение должна
 * открывать комнату здесь же, а не перезагружать вкладку.
 */
export function parseAppLink(href: string, bases: string[]): RouteTarget | null {
  const i = href.indexOf("#/room/");
  if (i < 0) return null;
  const head = href.slice(0, i);
  const norm = (u: string) => u.replace(/\/+$/, "");
  if (!bases.some((b) => norm(b) === norm(head))) return null;
  const r = parseLocationHash(href.slice(i));
  return r.roomId ? r : null;
}

/**
 * Абсолютная ссылка на событие В НАШЕМ КЛИЕНТЕ — то, что кладётся в
 * «Поделиться» и «Копировать ссылку» (патч Антона 2026-09-10 10:31:
 * «все должно открываться в нашем клиенте а не в элементе»).
 *
 * Хост НЕ хардкодим: window.location.origin — это devapp.algsuccess.com
 * на стенде и localhost у разработчика, и оба правильные.
 */
export function appPermalink(roomId: string, eventId?: string): string {
  return `${window.location.origin}${window.location.pathname}#${roomHash(roomId, eventId)}`;
}

/** Результат разбора matrix.to-ссылки. */
export type MatrixToTarget =
  | { kind: "user"; userId: string }
  | { kind: "room"; roomId: string; eventId: string | null }
  | { kind: "alias"; alias: string; eventId: string | null };

/**
 * Разобрать ссылку matrix.to в цель внутренней навигации.
 *
 * Принимаем и «https://matrix.to/#/…», и относительный вид «#/!room/$event»
 * (formatted_body реплая-цитаты у других клиентов). Возвращаем null для
 * всего прочего — такие ссылки остаются внешними и открываются как обычно.
 *
 * matrix.to допускает query (?via=server) после идентификаторов — отрезаем:
 * нам из него нужны только via-серверы для join по алиасу, а join мы всё
 * равно делаем через свой домашний сервер.
 */
export function parseMatrixTo(href: string): MatrixToTarget | null {
  let frag: string;
  if (href.startsWith("https://matrix.to/#/") || href.startsWith("http://matrix.to/#/")) {
    frag = href.slice(href.indexOf("#/") + 2);
  } else if (href.startsWith("#/")) {
    frag = href.slice(2);
  } else {
    return null;
  }
  frag = frag.split("?")[0];
  const segs = frag.split("/").filter((s) => s.length > 0).map(safeDecode);
  if (segs.length === 0) return null;

  const first = segs[0];
  const second = segs[1] ?? null;

  if (first.startsWith("@")) {
    return { kind: "user", userId: first };
  }
  if (first.startsWith("!")) {
    return { kind: "room", roomId: first, eventId: second };
  }
  if (first.startsWith("#")) {
    return { kind: "alias", alias: first, eventId: second };
  }
  return null;
}

/**
 * Найти ближайший <a href> от точки клика и, если это matrix.to-ссылка,
 * вернуть её цель. Всплытие вверх по DOM обязательно: клик может прийтись
 * по вложенному <b>/<span> внутри ссылки (пилюли упоминаний так и
 * размечены — текст внутри <a>).
 */
export function matrixToFromEvent(e: MouseEvent): MatrixToTarget | null {
  const el = (e.target as HTMLElement | null)?.closest?.("a[href]");
  if (!el) return null;
  const href = el.getAttribute("href") ?? "";
  return parseMatrixTo(href);
}
